import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { GiGuitar } from 'react-icons/gi';
import { Colors } from '../utils/colors';
import { Sizes } from '../utils/sizes';
import { currentPriceTier, upcomingPriceTiers, COURSE } from '../data/courseData';

function formatPriceBRL(cents) {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDateBR(iso) {
  const [, month, day] = iso.split('-');
  return `${day}/${month}`;
}

const Section = styled.section`
  width: 100%;
  background: linear-gradient(180deg, #0f0f0f 0%, #141414 100%);
  padding: 70px 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, ${Colors.yellow}, transparent);
  }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  gap: 60px;
  align-items: center;

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    flex-direction: column;
    gap: 36px;
  }
`;

const ImageSide = styled.div`
  flex-shrink: 0;
  width: 280px;

  @media (max-width: ${Sizes.mediaQueryBreak2}) {
    width: 220px;
  }

  img {
    width: 100%;
    border-radius: 12px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.6);
    border: 1px solid rgba(255, 215, 0, 0.15);
  }
`;

const ContentSide = styled.div`
  flex: 1;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 215, 0, 0.1);
  border: 1px solid rgba(255, 215, 0, 0.3);
  color: ${Colors.yellow};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  padding: 4px 12px;
  border-radius: 20px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  color: #fff;
  font-size: 2.2rem;
  font-weight: 800;
  line-height: 1.2;
  margin: 0 0 8px;

  span { color: ${Colors.yellow}; }

  @media (max-width: ${Sizes.mediaQueryBreak3}) {
    font-size: 1.7rem;
  }
`;

const Sub = styled.p`
  color: ${Colors.textSecondary};
  font-size: 1rem;
  line-height: 1.6;
  margin: 0 0 24px;
`;

const Topics = styled.ul`
  list-style: none;
  margin: 0 0 28px;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: ${Sizes.mediaQueryBreak3}) {
    grid-template-columns: 1fr;
  }

  li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    color: #fff;
    font-size: 0.9rem;

    &::before {
      content: '✓';
      color: ${Colors.yellow};
      font-weight: 700;
      flex-shrink: 0;
      margin-top: 1px;
    }
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;

  .price {
    color: ${Colors.yellow};
    font-size: 2rem;
    font-weight: 900;
    white-space: nowrap;

    @media (max-width: ${Sizes.mediaQueryBreak3}) {
      font-size: 1.6rem;
    }
  }

  .original {
    color: ${Colors.textSecondary};
    font-size: 0.95rem;
    text-decoration: line-through;
    white-space: nowrap;
  }

  .tag {
    background: rgba(255,215,0,0.12);
    color: ${Colors.yellow};
    font-size: 11px;
    font-weight: 700;
    padding: 3px 10px;
    border-radius: 20px;
    white-space: nowrap;
  }
`;

const Urgency = styled.p`
  color: ${Colors.yellow};
  font-size: 0.85rem;
  font-weight: 600;
  margin: -10px 0 20px;
`;

const Cta = styled.button`
  background: ${Colors.yellow};
  color: #0a0a0a;
  border: none;
  padding: 16px 40px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 800;
  cursor: pointer;
  letter-spacing: 0.5px;
  transition: transform 0.15s, box-shadow 0.15s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(255, 215, 0, 0.3);
  }

  @media (max-width: ${Sizes.mediaQueryBreak3}) {
    width: 100%;
    padding: 16px 24px;
  }
`;

const SectionCurso = () => {
  const navigate = useNavigate();
  const tier = currentPriceTier();
  const nextTier = upcomingPriceTiers()[0];
  const isLaunchPrice = tier.priceCents === COURSE.priceSchedule[0].priceCents;

  return (
    <Section>
      <Inner>
        <ImageSide>
          <img
            src="/capaEbooksLoja/capaCurso.png"
            alt="Curso de Fingerstyle Católico"
            onClick={() => navigate('/curso-de-fingerstyle')}
            style={{ cursor: 'pointer' }}
          />
        </ImageSide>

        <ContentSide>
          <Badge><GiGuitar size={14} /> Novo — Curso em vídeo</Badge>
          <Title>
            Curso de <span>Fingerstyle</span> Católico
          </Title>
          <Sub>
            Do básico ao avançado — aprenda a unir harmonia e melodia no violão
            e toque músicas católicas do jeito certo, com técnica e expressão.
          </Sub>

          <Topics>
            <li>Técnica de mão direita e esquerda</li>
            <li>Dedilhados mais usados no fingerstyle</li>
            <li>Como ler e usar tablaturas</li>
            <li>Campo harmônico e tonalidade</li>
            <li>Músicas fáceis, intermediárias e avançadas</li>
            <li>Como criar seu próprio fingerstyle</li>
            <li>Mais de 30 vídeo-aulas completas</li>
            <li>Acesso vitalício sem mensalidades</li>
          </Topics>

          <PriceRow>
            {/* Preço do PIX (R$ 147,00) como headline. */}
            <span className="price">{formatPriceBRL(14700)}</span>
            <span className="original">{formatPriceBRL(COURSE.originalPriceCents)}</span>
            <span className="tag">{isLaunchPrice ? 'LANÇAMENTO' : 'OFERTA POR TEMPO LIMITADO'}</span>
          </PriceRow>
          {nextTier && (
            <Urgency>
              Preço sobe para {formatPriceBRL(nextTier.priceCents)} em {formatDateBR(nextTier.effectiveFrom)} — garanta no valor de hoje
            </Urgency>
          )}

          <Cta onClick={() => navigate('/curso-de-fingerstyle')}>
            VER O CURSO COMPLETO
          </Cta>
        </ContentSide>
      </Inner>
    </Section>
  );
};

export default SectionCurso;
